'use client';

import {
  createContext,
  use,
  useCallback,
  useMemo,
  useState,
  type ReactNode
} from 'react';
import { DocumentDrawer } from '@/components/documents/one-list/document-drawer';
import { downloadFile, previewFile } from '@/lib/services/files/file-utils';
import { getFileTypeSlug } from '@/lib/services/files/utils';
import type { QuoteSource } from '@/lib/services/ai-analysis/types';
import {
  toReviewDocumentItem,
  type ReviewDocument
} from './compact-documents-review';
import { useReviewDocumentRecords } from './use-review-document-records';

/**
 * **Who can name and open the document behind a quote** (VALE-1417).
 *
 * VALE-1417 gave every AI-review hover card one shape, and immediately exposed
 * that only one of the four could fill it in. The contacts review row resolved
 * the file and passed a `sourceFileName` / `sourceFileType` / `onOpenDocument`
 * trio; the inventory review row and both record drawers passed the bare
 * `source` and nothing else. So an asset said `Informed by an uploaded
 * document` — the generic fallback for a file it could not resolve — with no
 * `View source`, while the contact beside it said `Informed by the will
 * (page 3)` and offered the link. One shape stating two different amounts is
 * not one shape.
 *
 * The gap predates the reshape and was simply invisible: every card used to
 * render the same generic grey line, so the two read alike.
 *
 * **Why a context rather than three more prop chains.** The resolution is four
 * lines and it was already written out twice (the contacts row, and the
 * deceased section before VALE-1417 removed its footnote) with a third variant
 * in `preview-matter-diff-card.tsx`. Threading it to the drawers would have
 * made five copies of one rule, and left the next popover free to forget it
 * again — which is exactly how three of the four came to be wrong. A review
 * surface states its documents ONCE, here, and every card under it can answer
 * the question. A card mounted with no provider (a story, a unit test, a
 * non-review host) degrades to what it does today.
 */

interface ReviewSourceDocumentsValue {
  documents?: ReviewDocument[];
  openDocument: (documentId: number) => void;
}

const ReviewSourceDocumentsContext =
  createContext<ReviewSourceDocumentsValue | null>(null);

export interface ReviewSourceDocumentsProviderProps {
  /** The review's uploaded files — what a quote's `fileId` is resolved against. */
  documents?: ReviewDocument[];
  /**
   * The matter the review is for, so an opened file carries its real record.
   * Absent on the create-matter surface, where there is no matter yet.
   */
  matterId?: number;
  children: ReactNode;
}

/**
 * States a review surface's documents once, and owns the reader they open in.
 *
 * **The drawer lives here rather than in the contacts list.** It used to sit
 * inside `CompactContactsReview`, which meant the assets list beside it had no
 * way to open anything at all — the reason an asset's card offered no `View
 * source` even once it could name the file. One reader per review surface is
 * also what keeps a file reached from a contact and the same file reached from
 * an asset showing the same record and the same note (VALE-1250's rule, now
 * enforced by there being one drawer instead of one per list).
 */
export function ReviewSourceDocumentsProvider({
  documents,
  matterId,
  children
}: ReviewSourceDocumentsProviderProps) {
  const [openDocId, setOpenDocId] = useState<number | null>(null);
  const openDoc = documents?.find((d) => d.id === openDocId) ?? null;
  const documentRecords = useReviewDocumentRecords(matterId);

  /*
    Memoised so the item's identity is stable across renders: the drawer holds
    the last document it was given in state, and a fresh object on every render
    replaces it on every render — which, while the note editor is open, is a
    re-derivation of the field under the reader's cursor.
  */
  const openDocItem = useMemo(
    () =>
      openDoc ? documentRecords.resolve(toReviewDocumentItem(openDoc)) : null,
    [openDoc, documentRecords]
  );

  const openDocument = useCallback((documentId: number) => {
    setOpenDocId(documentId);
  }, []);

  const value = useMemo<ReviewSourceDocumentsValue>(
    () => ({ documents, openDocument }),
    [documents, openDocument]
  );

  return (
    <ReviewSourceDocumentsContext value={value}>
      {children}
      <DocumentDrawer
        doc={openDocItem}
        onOpenChange={(next) => {
          if (!next) setOpenDocId(null);
        }}
        onNotesChange={documentRecords.onNotesChange}
        onCertifiedCopyChange={documentRecords.onCertifiedCopyChange}
        /*
          Bound to the shared helpers rather than threaded down from the
          surface: the drawer renders `Download` unconditionally and offers
          `Open in new tab` for a file it cannot render, so with no handlers
          both were controls that silently did nothing. Nothing here needs the
          surface's own callback — `downloadFile` and `previewFile` are the same
          two URLs every other consumer uses, so a prop chain through two
          section components would only restate them.
        */
        onDownload={downloadFile}
        onOpenInNewTab={previewFile}
      />
    </ReviewSourceDocumentsContext>
  );
}

/** The surface's documents and reader, or `null` outside a review surface. */
export function useReviewSourceDocuments(): ReviewSourceDocumentsValue | null {
  return use(ReviewSourceDocumentsContext);
}

export interface ResolvedQuoteSourceFile {
  sourceFileName?: string;
  sourceFileType?: string;
  onOpenDocument?: (documentId: number) => void;
}

/**
 * What a hover card needs to say where a quote came from, and offer the way in.
 *
 * Returns empties outside a review surface, so a card that is not on one keeps
 * rendering exactly what it renders today rather than throwing.
 */
export function useQuoteSourceFile(
  source: QuoteSource | undefined
): ResolvedQuoteSourceFile {
  const ctx = useReviewSourceDocuments();

  return useMemo(() => {
    if (!ctx || source?.type !== 'document') return {};
    const file = ctx.documents?.find((doc) => doc.id === source.fileId);
    return {
      sourceFileName: file?.displayName ?? file?.originalFilename,
      /*
        Asked of the shared resolver rather than reading `fileType` straight off
        the review record — the two fields the review shape carries are a
        document type and a file type, and which of them names the slug depends
        on where the record came from (VALE-1196).
      */
      sourceFileType: getFileTypeSlug(
        file?.documentType ?? file?.fileType ?? ''
      ),
      onOpenDocument: ctx.openDocument
    };
  }, [ctx, source]);
}
